import { IconButton } from '@mui/material'
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import makeStyles from '@mui/styles/makeStyles'
import { useAuth } from 'Components/Auth'
import PropTypes from 'prop-types'
import { useFollowingsQuery, useFollowMutation, useUnfollowMutation } from 'Queries/Users'
import React from 'react'
import { Else, If, Then, When } from 'react-if'
import FAIcon from 'Theme/FAIcon'
import { FA_ICONS } from 'Theme/font-awesome-icon-constants'

import { navigateToLoginModal, navigateToProfilePage } from '../../Scenes/navigators'
import JudgeVotedMaps from '../ContestComponents/JudgeVotedMaps'
import ButtonFollow from './Buttons/ButtonFollow'
import ButtonUnFollow from './Buttons/ButtonUnFollow'

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'space-between',
    boxShadow: 'none',
  },
  header: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'stretch',
    alignItems: 'center',
  },
  title: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    flex: '1 1',
    marginLeft: theme.spacing(2),
  },
  name: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
}))

function UserCard({
  src,
  alt,
  name,
  id: ownerId,
  judgeId,
  description,
  isPreview = false,
  isJudge = false,
  isPublishedRound = false,
  mapsCount = 0,
  awardsCount = 0,
  children,
  isMobile = false,
  contestRelation,
  isHaloWaypoint,
  haloWaypointUser,
  userRole,
  isBigButton = false,
  isWithCounter = true,
  isDeletedUser = false,
  isContestJudge = false,
  hideMaps = false,
  ...otherProps
}) {
  const { id, isAuthorized } = useAuth()
  const { data: userFollowing = [] } = useFollowingsQuery()
  const { mutate: follow } = useFollowMutation()
  const { mutate: unfollow } = useUnfollowMutation()

  const classes = useStyles()

  const isOwner = id === ownerId
  const isOwnerLoaded = !!ownerId
  const showFollowButton = isOwnerLoaded && !isOwner && !isDeletedUser
  const isUserFollowingMapOwner = !!userFollowing.find((item) => item.user_id === ownerId)

  const handleFollowClick = () => {
    if (!isAuthorized) {
      navigateToLoginModal()
    } else {
      isUserFollowingMapOwner ? unfollow(ownerId) : follow(ownerId)
    }
  }

  const goToUserProfilePage = () => {
    !isPreview && ownerId && navigateToProfilePage(ownerId)
  }

  const handleNavigateClick = () => {
    if (isHaloWaypoint) return
    goToUserProfilePage()
  }

  return (
    <Box
      className={classes.root}
      component={Paper}
      {...otherProps}
      sx={{ backgroundColor: isHaloWaypoint && '#0000001A' }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
        }}
      >
        <Box
          display="flex"
          className={classes.header}
          p={2}
          sx={{ cursor: !isPreview && !isHaloWaypoint && 'pointer' }}
          onClick={handleNavigateClick}
        >
          <If condition={isHaloWaypoint}>
            <Then>
              <FAIcon
                iconClassName={FA_ICONS.XBOX}
                color={'text.primary'}
                iconFontSize={34}
              />
            </Then>
            <Else>
              <Box
                component={Avatar}
                alt={alt}
                src={src}
              />
            </Else>
          </If>

          <Box
            display="flex"
            flexDirection={'column'}
            alignItems="center"
            ml={1}
            className={classes.title}
          >
            <Typography
              variant="h6"
              color="text.primary"
              sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '110px' }}
            >
              <If condition={isHaloWaypoint}>
                <Then>{haloWaypointUser?.display_name}</Then>
                <Else>{!!isDeletedUser ? 'Deleted user' : name}</Else>
              </If>
            </Typography>
            <Box
              component={Typography}
              variant="caption"
            >
              {description}
            </Box>
            <Typography
              sx={(theme) => ({
                ...theme.typography.caption,
                color: 'action.active',
                textTransform: 'none',
              })}
            >
              <If condition={isWithCounter}>
                <Then>
                  <If condition={isHaloWaypoint}>
                    <Then>{haloWaypointUser?.gamer_tag}</Then>
                    <Else>{!!isDeletedUser ? 'Map Author' : `${mapsCount} maps`}</Else>
                  </If>
                </Then>
              </If>
              <If condition={userRole}>
                <Then>{userRole}</Then>
              </If>
            </Typography>
          </Box>
        </Box>

        <When condition={showFollowButton && !isBigButton}>
          <Box sx={{ paddingRight: 2 }}>
            <If condition={!isUserFollowingMapOwner}>
              <Then>
                <IconButton
                  onClick={handleFollowClick}
                  disabled={!isOwnerLoaded || isPreview}
                >
                  <FAIcon
                    iconFontSize={16}
                    iconClassName={FA_ICONS.USER_PLUS}
                    color="secondary.main"
                  />
                </IconButton>
              </Then>
              <Else>
                {isContestJudge ? (
                  <IconButton
                    onClick={handleFollowClick}
                    disabled={!isOwnerLoaded || isPreview}
                  >
                    <FAIcon
                      iconFontSize={16}
                      iconClassName={FA_ICONS.USER_MINUS}
                      color="text.primary"
                    />
                  </IconButton>
                ) : (
                  <IconButton onClick={handleNavigateClick}>
                    <FAIcon
                      iconFontSize={16}
                      iconClassName={FA_ICONS.CARET_RIGHT}
                      color="secondary.main"
                    />
                  </IconButton>
                )}
              </Else>
            </If>
          </Box>
        </When>
      </Box>

      {!hideMaps && (
        <JudgeVotedMaps
          contestRelation={contestRelation}
          ownerId={judgeId}
          isJudge={isJudge}
          isPublishedRound={isPublishedRound}
        />
      )}

      <When condition={showFollowButton && isBigButton}>
        <If condition={!isUserFollowingMapOwner}>
          <Then>
            <ButtonFollow
              onClick={handleFollowClick}
              disabled={!isOwnerLoaded || isPreview}
              style={{ width: '228px', marginBottom: '16px' }}
            />
          </Then>
          <Else>
            <ButtonUnFollow
              onClick={handleFollowClick}
              disabled={!isOwnerLoaded || isPreview}
              style={{ width: '228px', marginBottom: '16px' }}
            />
          </Else>
        </If>
      </When>
      {children}
    </Box>
  )
}

// TODO most of the types should be required and component usage must be fixed with Skeleton for empty state
UserCard.propTypes = {
  src: PropTypes.string,
  alt: PropTypes.string,
  name: PropTypes.string,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  description: PropTypes.string,
  userRole: PropTypes.string,
  isPreview: PropTypes.bool,
  mapsCount: PropTypes.number,
  awardsCount: PropTypes.number,
  contestRelation: PropTypes.object,
}

export default UserCard
